/**
 * @fileoverview Document Upload Banner Component
 * @description Shows a persistent warning banner for users with PENDING_DOCUMENT_UPLOAD status
 * Guides users to upload required legal documents to complete their institution profile
 */
import React, { useState, useEffect } from "react";
import { Box, IconButton, Typography, Button, Slide, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { intl } from "dmsbasereact";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import { APP_ROUTES } from "../../utils/NamedRoutes";

const SESSION_STORAGE_KEY = "documentUploadBannerDismissed";

// Warning theme colors
const COLORS = {
    background: "#FFFBEB",
    text: "#92400E",
    border: "#FCD34D",
    hoverBg: "rgba(146, 64, 14, 0.08)",
};

/**
 * DocumentUploadBanner - Shows a warning banner for pending document uploads
 * @param {Object} props - Component props
 * @param {boolean} props.isVisible - Whether the banner should be shown (based on account status)
 * @param {string} props.institutionName - Name of the institution (optional)
 */
export const DocumentUploadBanner = ({ isVisible, institutionName }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [dismissed, setDismissed] = useState(false);
    const [mounted, setMounted] = useState(false);

    // Check sessionStorage on mount to see if banner was dismissed this session
    useEffect(() => {
        const wasDismissed = sessionStorage.getItem(SESSION_STORAGE_KEY) === "true";
        setDismissed(wasDismissed);
        setMounted(true);
    }, []);

    // Don't render anything until mounted (to check sessionStorage)
    if (!mounted) {
        return null;
    }

    // Don't show if not visible or if dismissed this session
    if (!isVisible || dismissed) {
        return null;
    }

    const handleDismiss = () => {
        sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
        setDismissed(true);
    };

    const handleNavigateToDocuments = () => {
        // Navigate to Institution Central page with Documents tab selected (tab index 1)
        navigate(`${APP_ROUTES.InstitutionCentral}?tab=1`);
    };

    const message = institutionName
        ? intl
              .get("DocumentUploadBanner.MessageWithName", { institutionName })
              .d(`Your institution profile for ${institutionName} is incomplete. Please upload the required legal documents to access all system features.`)
        : intl
              .get("DocumentUploadBanner.Message")
              .d("Your institution profile is incomplete. Please upload the required legal documents to access all system features.");

    return (
        <Slide direction="down" in={!dismissed} mountOnEnter unmountOnExit>
            <Box
                sx={{
                    width: "100%",
                    minHeight: isMobile ? "auto" : 48,
                    maxHeight: isMobile ? "auto" : 56,
                    backgroundColor: COLORS.background,
                    borderBottom: `1px solid ${COLORS.border}`,
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "stretch" : "center",
                    justifyContent: "center",
                    px: 2,
                    py: isMobile ? 1.5 : 1,
                    position: "relative",
                }}
            >
                {/* Content Container */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        justifyContent: "center",
                        gap: isMobile ? 1 : 2,
                        maxWidth: 1200,
                        width: "100%",
                        pr: isMobile ? 4 : 5,
                    }}
                >
                    {/* Icon and Message */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: isMobile ? "flex-start" : "center",
                            gap: 1,
                            flexWrap: "wrap",
                        }}
                    >
                        <ErrorOutlineIcon
                            sx={{
                                color: COLORS.text,
                                fontSize: "1.25rem",
                                mt: isMobile ? 0.25 : 0,
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: COLORS.text,
                                fontWeight: 400,
                                flex: 1,
                            }}
                        >
                            <Box component="span" sx={{ fontWeight: 700 }}>
                                {intl.get("DocumentUploadBanner.ActionRequired").d("Action Required")}:
                            </Box>{" "}
                            {message}
                        </Typography>
                    </Box>

                    {/* Action Button/Link */}
                    {isMobile ? (
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={handleNavigateToDocuments}
                            sx={{
                                color: COLORS.text,
                                borderColor: COLORS.text,
                                backgroundColor: "transparent",
                                textTransform: "none",
                                fontWeight: 600,
                                mt: 0.5,
                                "&:hover": {
                                    backgroundColor: COLORS.hoverBg,
                                    borderColor: COLORS.text,
                                },
                            }}
                        >
                            {intl.get("DocumentUploadBanner.ClickHere").d("Click Here to Complete Setup")}
                        </Button>
                    ) : (
                        <Typography
                            component="button"
                            variant="body2"
                            onClick={handleNavigateToDocuments}
                            sx={{
                                color: COLORS.text,
                                fontWeight: 600,
                                textDecoration: "underline",
                                cursor: "pointer",
                                background: "none",
                                border: "none",
                                padding: 0,
                                whiteSpace: "nowrap",
                                "&:hover": {
                                    textDecoration: "none",
                                },
                            }}
                        >
                            [{intl.get("DocumentUploadBanner.ClickHere").d("Click Here to Complete Setup")}]
                        </Typography>
                    )}
                </Box>

                {/* Close Button */}
                <IconButton
                    size="small"
                    aria-label={intl.get("Close").d("Close")}
                    onClick={handleDismiss}
                    disableRipple
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: isMobile ? 8 : "50%",
                        transform: isMobile ? "none" : "translateY(-50%)",
                        color: COLORS.text,
                        width: 24,
                        height: 24,
                        minWidth: 24,
                        padding: 0,
                        borderRadius: "4px",
                        "&:hover": {
                            backgroundColor: COLORS.hoverBg,
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        </Slide>
    );
};

export default DocumentUploadBanner;
